import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import TikTokCard from "./TikTokCard";
import "./style.css";
import axios from "axios";

export default function TikTokFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);

  const filterByUser =
    location.state?.filterByUser ||
    new URLSearchParams(location.search).get("userId") ||
    null;
  const filterByUsername =
    location.state?.filterByUsername ||
    new URLSearchParams(location.search).get("username") ||
    null;

  const fetchPosts = useCallback(async (pageNum = 1) => {
    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: 10,
        ...(filterByUser && { userId: filterByUser }),
      });
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/tiktok?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (pageNum === 1) {
        setPosts(data.posts || []);
      } else {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
      }
      // Calculate hasMore from pagination
      const pagination = data.pagination || {};
      setHasMore(pagination.page < pagination.pages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching TikTok feed:", error);
      setLoading(false);
    }
  }, [user.token, filterByUser]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const cardHeight = window.innerHeight;
    const newIndex = Math.round(scrollTop / cardHeight);

    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < posts.length) {
      setActiveIndex(newIndex);
    }

    // Load more when near end
    if (
      newIndex >= posts.length - 3 &&
      hasMore &&
      !loading
    ) {
      setPage((prev) => prev + 1);
      fetchPosts(page + 1);
    }
  }, [activeIndex, posts.length, hasMore, loading, page, fetchPosts]);

  const scrollToIndex = (index) => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: index * window.innerHeight,
        behavior: "smooth",
      });
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowDown" && activeIndex < posts.length - 1) {
      scrollToIndex(activeIndex + 1);
    } else if (e.key === "ArrowUp" && activeIndex > 0) {
      scrollToIndex(activeIndex - 1);
    }
  }, [activeIndex, posts.length]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (loading && posts.length === 0) {
    return (
      <div className="tiktok_feed_loading">
        <div className="tiktok_loader"></div>
        <span>Carregando vídeos...</span>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="tiktok_feed_empty">
        <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
        </svg>
        <h3>Nenhum vídeo TikTok</h3>
        <p>Seja o primeiro a publicar um vídeo curto!</p>
      </div>
    );
  }

  return (
    <div
      className="tiktok_feed"
      ref={containerRef}
      onScroll={handleScroll}
    >
      {filterByUser && (
        <div className="feed_user_filter_banner">
          <div className="feed_user_filter_content">
            <span className="feed_user_filter_icon">👤</span>
            <span className="feed_user_filter_text">
              Mostrando apenas publicações de <strong>{filterByUsername || "usuário"}</strong> no TikTok
            </span>
          </div>
        </div>
      )}
      {posts.map((post, index) => (
        <TikTokCard
          key={post._id}
          post={post}
          user={user}
          isActive={index === activeIndex}
        />
      ))}

      {loading && (
        <div className="tiktok_loading_more">
          <div className="tiktok_loader small"></div>
        </div>
      )}

      {/* Navigation dots */}
      <div className="tiktok_dots">
        {posts.slice(0, 10).map((_, index) => (
          <div
            key={index}
            className={`tiktok_dot ${index === activeIndex ? "active" : ""}`}
            onClick={() => scrollToIndex(index)}
          />
        ))}
        {posts.length > 10 && <div className="tiktok_dot more">...</div>}
      </div>
    </div>
  );
}
